import { itx } from "../../../src/translationItx/itx";
import { pdfHeaderText } from "../../../src/pdf/pdfTagName";
import type {
  C1_ReportButton,
  C1_ReportConsumer,
  C1_ReportFloor,
  C1_ReportGroup,
  C1_ProjectReportData,
} from "../lib/C1_projectReportData";
import { C1_EloItem_Type, ChannelType, C1_EloItem_PlugType, C1_EloItem_Voltage } from "../types/C1_EloTypes";
import { compareTags } from "../../../src/utils/tagSort";
import { getButtonProductDefinition } from "../data/C1_ProductDefinitions";
import C1_eloItemsRegistry from "../data/C1_eloItemsRegistry";

export const C1_ROOMBOOK_SUMMARY_COLUMNS = [
  { id: "buttonSimple", get group() { return itx("menubar.buttons", "Taster"); }, get label() { return itx("SettingsScreen.simple", "Einfach").toLowerCase(); } },
  { id: "buttonTouch", get group() { return itx("menubar.buttons", "Taster"); }, get label() { return itx("c1.ui.Touch", "Touch"); } },
  { id: "buttonPanel", get group() { return itx("menubar.buttons", "Taster"); }, get label() { return itx("panelDialog.panel", "Panel"); } },
  { id: "heating", get group() { return itx("components.Heating", "Heizung"); }, label: "" },
  { id: "blind", get group() { return itx("raumbuch.shade", "Beschattung"); }, get label() { return itx("ELCBTreeviewModel.rollo", "Rollo"); } },
  { id: "jalousie", get group() { return itx("raumbuch.shade", "Beschattung"); }, get label() { return itx("toolBar.jalousieShort", "Jalousie"); } },
  { id: "lightSwitch", get group() { return itx("components.plural.Light", "Leuchten"); }, get label() { return itx("c1.ui.geschaltet", "geschaltet"); } },
  { id: "lightDimmable", get group() { return itx("components.plural.Light", "Leuchten"); }, get label() { return itx("c1.ui.gedimmt", "gedimmt"); } },
  { id: "lightTunableWhite", get group() { return itx("components.plural.Light", "Leuchten"); }, label: "Tunable White" },
  { id: "lightRgb", get group() { return itx("components.plural.Light", "Leuchten"); }, label: "RGB" },
  { id: "lightDaliSwitch", get group() { return itx("c1.ui.Leuchten_DALI", "Leuchten DALI"); }, get label() { return itx("c1.ui.geschaltet", "geschaltet"); } },
  { id: "lightDaliDimmable", get group() { return itx("c1.ui.Leuchten_DALI", "Leuchten DALI"); }, get label() { return itx("c1.ui.gedimmt", "gedimmt"); } },
  { id: "lightDaliTunableWhite", get group() { return itx("c1.ui.Leuchten_DALI", "Leuchten DALI"); }, label: "Tunable White" },
  { id: "lightDaliRgb", get group() { return itx("c1.ui.Leuchten_DALI", "Leuchten DALI"); }, label: "RGB" },
  { id: "plugGroups", get group() { return itx("toolBar.outletShort", "Steckdose"); }, get label() { return itx("components.plural.group", "Gruppen"); } },
  { id: "plugFixed", get group() { return itx("toolBar.outletShort", "Steckdose"); }, get label() { return itx("TopologyLineView.duration", "Dauer"); } },
  { id: "plugSwitched", get group() { return itx("toolBar.outletShort", "Steckdose"); }, get label() { return itx("c1.ui.geschaltet", "geschaltet"); } },
  { id: "plugLan", get group() { return itx("toolBar.outletShort", "Steckdose"); }, label: "LAN" },
  { id: "plugLan2", get group() { return itx("toolBar.outletShort", "Steckdose"); }, label: "LAN 2" },
  { id: "plugUsb", get group() { return itx("toolBar.outletShort", "Steckdose"); }, label: "USB" },
  { id: "power230", get group() { return itx("components.plural.Supply", "Stromversorgung"); }, label: "230 V" },
  { id: "power400", get group() { return itx("components.plural.Supply", "Stromversorgung"); }, label: "400 V" },
] as const;

export type C1_RoombookSummaryColumn = (typeof C1_ROOMBOOK_SUMMARY_COLUMNS)[number];
export type C1_RoombookSummaryColumnId = C1_RoombookSummaryColumn["id"];
export type C1_RoombookSummaryValues = Record<C1_RoombookSummaryColumnId, number>;

export type C1_RoombookSummaryRow = {
  id: string;
  label: string;
  values: C1_RoombookSummaryValues;
  /** Anzahl der Objekte ohne wiredTogetherWith. */
  independentlyWiredValues: C1_RoombookSummaryValues;
  total: number;
};

export type C1_RoombookFloorSummary = {
  id: string;
  label: string;
  rows: C1_RoombookSummaryRow[];
  total: C1_RoombookSummaryRow;
};

export type C1_RoombookProjectSummary = {
  floors: C1_RoombookFloorSummary[];
  total: C1_RoombookSummaryRow;
};

export function getC1RoombookWiringCountExplanation(): string {
  return itx("c1.ui.Die_Zahl_in_Klammern_gibt_die_Gesamtzahl_der_Leuchten_beziehungsweise_Steckdosen_an_die_Zahl_davor_gibt_die_Anzahl_der_g", "Die Zahl in Klammern gibt die Gesamtzahl der Leuchten beziehungsweise Steckdosen an; die Zahl davor gibt die Anzahl der geschalteten Gruppen oder einzeln geschalteten Objekte an.");
}

function compareSummaryText(left: unknown, right: unknown): number {
  return compareTags(left, right);
}

function emptyValues(): C1_RoombookSummaryValues {
  return Object.fromEntries(C1_ROOMBOOK_SUMMARY_COLUMNS.map((column) => [column.id, 0])) as C1_RoombookSummaryValues;
}

function itemAmount(item: C1_ReportConsumer | C1_ReportButton): number {
  const details = item.details && !("shading" in item.details) ? item.details : undefined;
  return item.count ?? details?.itemCount ?? 1;
}

function buttonColumn(item: C1_ReportButton): C1_RoombookSummaryColumnId {
  if (item.details?.buttonType === "panel") return "buttonPanel";
  return getButtonProductDefinition(item.details?.buttonType)?.uiTemplate === "simple" ? "buttonSimple" : "buttonTouch";
}

function columnFor(item: C1_ReportConsumer): C1_RoombookSummaryColumnId | undefined {
  const details = item.details && !("shading" in item.details) ? item.details : undefined;
  if (item.consumerType === C1_EloItem_Type.Panel) return "buttonPanel";
  if (item.consumerType === C1_EloItem_Type.Heating) return "heating";
  if (item.consumerType === C1_EloItem_Type.Jalousie) return "jalousie";
  if (item.consumerType === C1_EloItem_Type.Blind) return "blind";
  if (item.consumerType === C1_EloItem_Type.Light) {
    const dali = details?.isDali === true;
    if (details?.channelType === ChannelType.Dimmer) return dali ? "lightDaliDimmable" : "lightDimmable";
    if (details?.channelType === ChannelType.TuneableWhite) return dali ? "lightDaliTunableWhite" : "lightTunableWhite";
    if (details?.channelType === ChannelType.Rgb) return dali ? "lightDaliRgb" : "lightRgb";
    return dali ? "lightDaliSwitch" : "lightSwitch";
  }
  if (item.consumerType === C1_EloItem_Type.Plug) {
    if (details?.plugType?.startsWith(C1_EloItem_PlugType.V230Switch)) return "plugSwitched";
    if (details?.plugType === C1_EloItem_PlugType.LAN) return "plugLan";
    if (details?.plugType === C1_EloItem_PlugType.LAN2) return "plugLan2";
    if (details?.plugType === C1_EloItem_PlugType.USB) return "plugUsb";
    return details?.plugType === C1_EloItem_PlugType.V230 ? "plugFixed" : undefined;
  }
  if (item.consumerType === C1_EloItem_Type.FixedPower) {
    return details?.voltage === C1_EloItem_Voltage.V400 ? "power400" : "power230";
  }
  return undefined;
}

function totalValues(rows: C1_RoombookSummaryRow[]): C1_RoombookSummaryValues {
  const values = emptyValues();
  rows.forEach((row) => C1_ROOMBOOK_SUMMARY_COLUMNS.forEach((column) => (values[column.id] += row.values[column.id])));
  return values;
}

function plugGroupCount(groups: C1_ReportGroup[]): number {
  return groups.reduce((count, group) => {
    const definitionId = group.definitionId?.replace(/-\d+$/, "");
    const isPlugGroup = definitionId
      ? C1_eloItemsRegistry.get(definitionId)?.type === C1_EloItem_Type.PlugGroup
      : false;
    return count + (isPlugGroup ? 1 : 0) + plugGroupCount(group.groups);
  }, 0);
}

function totalIndependentValues(rows: C1_RoombookSummaryRow[]): C1_RoombookSummaryValues {
  const values = emptyValues();
  rows.forEach((summaryRow) =>
    C1_ROOMBOOK_SUMMARY_COLUMNS.forEach(
      (column) => (values[column.id] += summaryRow.independentlyWiredValues[column.id]),
    ),
  );
  return values;
}

function row(
  id: string,
  label: string,
  values: C1_RoombookSummaryValues,
  independentlyWiredValues: C1_RoombookSummaryValues = values,
): C1_RoombookSummaryRow {
  return {
    id,
    label,
    values,
    independentlyWiredValues,
    total: C1_ROOMBOOK_SUMMARY_COLUMNS.reduce((sum, column) => sum + values[column.id], 0),
  };
}

function summarizeFloor(floor: C1_ReportFloor): C1_RoombookFloorSummary {
  const rows = [...floor.rooms]
    .sort(
      (left, right) =>
        compareSummaryText(left.tag, right.tag) ||
        compareSummaryText(left.name, right.name) ||
        compareSummaryText(left.id, right.id),
    )
    .map((room) => {
      const values = emptyValues();
      const independentlyWiredValues = emptyValues();
      values.plugGroups = plugGroupCount(room.groups);
      independentlyWiredValues.plugGroups = values.plugGroups;
      room.consumers.forEach((item) => {
        const column = columnFor(item);
        if (column) {
          const amount = itemAmount(item);
          values[column] += amount;
          if (!item.wiredTogetherWith) independentlyWiredValues[column] += amount;
        }
      });
      room.sensors.forEach((item) => {
        const column = columnFor(item);
        if (column) values[column] += itemAmount(item);
      });
      room.buttons.forEach((item) => {
        const column = buttonColumn(item);
        const amount = itemAmount(item);
        values[column] += amount;
        independentlyWiredValues[column] += amount;
      });
      room.sensors.forEach((item) => {
        const column = columnFor(item);
        if (column) independentlyWiredValues[column] += itemAmount(item);
      });
      return row(room.id, pdfHeaderText(room), values, independentlyWiredValues);
    });
  const label = pdfHeaderText(floor);
  return {
    id: floor.id,
    label,
    rows,
    total: row(`${floor.id}-total`, itx("c1.ui.Summe_value1", "Summe {{value1}}", { "{{value1}}": String(label) }), totalValues(rows), totalIndependentValues(rows)),
  };
}

// Wiring semantics are determined by stable IDs, never translated column headings.
function hasIndependentWiringCount(id: C1_RoombookSummaryColumnId): boolean {
  return id.startsWith("plug") || (id.startsWith("light") && !id.startsWith("lightDali"));
}

export function formatC1RoombookSummaryValue(
  summaryRow: C1_RoombookSummaryRow,
  column: C1_RoombookSummaryColumn,
): string {
  const total = summaryRow.values[column.id];
  if (!total) return "–";
  if (!hasIndependentWiringCount(column.id)) return String(total);
  const independentlyWired = summaryRow.independentlyWiredValues[column.id];
  return independentlyWired === total ? String(total) : `${independentlyWired} (${total})`;
}

export function hasC1RoombookSummaryWiringCounts(summary: C1_RoombookProjectSummary): boolean {
  return C1_ROOMBOOK_SUMMARY_COLUMNS.some(
    (column) =>
      hasIndependentWiringCount(column.id) &&
      summary.total.values[column.id] !== summary.total.independentlyWiredValues[column.id],
  );
}

export function visibleC1RoombookSummaryColumns(summary: C1_RoombookProjectSummary) {
  return C1_ROOMBOOK_SUMMARY_COLUMNS.filter((column) => summary.total.values[column.id] > 0);
}

export function createC1RoombookProjectSummary(project: C1_ProjectReportData): C1_RoombookProjectSummary {
  const floors = [...project.floors]
    .sort(
      (left, right) =>
        left.level - right.level ||
        compareSummaryText(left.tag, right.tag) ||
        compareSummaryText(left.name, right.name) ||
        compareSummaryText(left.id, right.id),
    )
    .map(summarizeFloor);
  return {
    floors,
    total: row(
      "project-total",
      itx("c1.ui.Summe_Projekt", "Summe Projekt"),
      totalValues(floors.map((floor) => floor.total)),
      totalIndependentValues(floors.map((floor) => floor.total)),
    ),
  };
}
